import React, { useState, useMemo } from 'react';
import * as XLSX from 'xlsx';

// --- IMPORTACIÓN DE NUESTRAS UTILIDADES DE SEGUROS ---
import { parseCurrency, formatCurrencyUniversal } from '../../../../utils/segurosUtils';

// --- ICONOS LOCALES PREMIUM ---
const IconUser = () => (
    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
);

const IconFolder = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
);

const IconBack = () => (
    <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
);

const IconDownload = () => (
    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
    </svg>
);

// LISTA ESTÁNDAR DE ESTADOS EN TRÁMITE PENDIENTE
const ESTADOS_PENDIENTES = [
    'RECOTIZANDO', 
    'CONSULTE SI RENUEVA', 
    'REVISION', 
    'PENDIENTE DE ENVIO', 
    'PLAN DE PAGO', 
    'DEUDA INFORMADA'
];

// OPERADORES AUTORIZADOS A COBRAR INCENTIVO
const OPERADORES_CON_INCENTIVO = [
    'VANESA MORRONE',
    'MARIEL BUEDO',
    'ROCIO RAMOS',
    'CAROLINA MANSILLA',
    'ALEXIS MARTINEZ'
];

/**
 * RendimientoRenovacionesVendedores - Módulo analítico enfocado en producción de renovaciones, efectividad, incentivos y bajas posteriores.
 */
const RendimientoRenovacionesVendedores = ({ registros, isDbLoaded, porcentajeIncentivo = 0.3 }) => {
    const [selectedOperador, setSelectedOperador] = useState('');
    const [subTab, setSubTab] = useState('CARTERA_COMPLETA'); // CARTERA_COMPLETA | NO_RENUEVA | PENDIENTES | SIN_GESTION | ANULADAS_NUEVAS

    // --- HELPER: EVALUAR ANULACIONES ---
    const esAnuladaEmision = (item) => {
        const estEmisionOrig = String(item.estadoEmision || '').toUpperCase().trim();
        return estEmisionOrig === 'ANULADA' || estEmisionOrig === 'CANCELADA';
    };

    const esAnuladaNueva = (item) => {
        const pNueva = item.polizaNuevaObj || {};
        const estEmisionN = String(item.estadoEmisionNueva || pNueva.estadoEmision || '').toUpperCase().trim();
        return estEmisionN === 'ANULADA' || estEmisionN === 'CANCELADA';
    };

    // --- HELPERS DE VALIDACIÓN DE INCENTIVO ---
    const operadorTieneIncentivo = (nombreOperador) => {
        if (!nombreOperador) return false;
        return OPERADORES_CON_INCENTIVO.includes(String(nombreOperador).trim().toUpperCase());
    };

    const esCanalDirecto = (item) => {
        const pNueva = item.polizaNuevaObj || {};
        const canal = String(item.canalNueva || pNueva.canal || item.canal || '').trim().toUpperCase();
        return canal === 'DIRECTO';
    };

    // --- HELPERS DE LECTURA ESTRICTA POR CAMPO ---
    const getGestorEntrada = (p) => {
        const val = p.gestionadoPor;
        return (val && String(val).trim() !== '' && String(val).trim() !== 'null') ? String(val).trim() : 'SIN ASIGNAR';
    };

    const getEmisorSalida = (p) => {
        const val = p.quienRenovoNuevo;
        return (val && String(val).trim() !== '' && String(val).trim() !== 'null') ? String(val).trim() : 'SIN ASIGNAR';
    };

    // Quien gestionó la baja de la póliza nueva emitida
    const getGestorBajaNueva = (p) => {
        const pNueva = p.polizaNuevaObj || {};
        const val = p.quienGestionoAnulacionNueva || pNueva.quienGestionoAnulacion || pNueva.quienGestiono || p.quienGestiono;
        return (val && String(val).trim() !== '' && String(val).trim() !== 'null') ? String(val).trim() : 'SIN ASIGNAR';
    };

    // --- 1. CUADRO COMPARATIVO GENERAL DE RENOVACIONES POR RESPONSABLE ---
    const matrizOperadores = useMemo(() => {
        if (!isDbLoaded || !registros) return [];
        
        const mapOperadores = {};

        const getOp = (nombre) => {
            if (!mapOperadores[nombre]) {
                mapOperadores[nombre] = { 
                    nombre, 
                    polizasRenovadas: 0, 
                    polizasNoRenueva: 0,
                    polizasPendientes: 0,
                    polizasSinGestion: 0,
                    polizasAnuladasNuevas: 0,
                    primaARS: 0, 
                    primaUSD: 0,
                    incentivoARS: 0,
                    incentivoUSD: 0
                };
            }
            return mapOperadores[nombre];
        };

        registros.forEach(p => {
            const opGestor = getGestorEntrada(p);
            const opEmisor = getEmisorSalida(p);
            const opBajaNueva = getGestorBajaNueva(p);

            const estadoGestionRaw = String(p.estadoGestionRenovacion || '').trim();
            const estadoGestion = estadoGestionRaw.toUpperCase();
            const tipoRenovacion = String(p.tipoRenovacion || p.formaRenovacion || '').trim().toUpperCase();

            const esNoRenueva = (estadoGestion === 'NO RENUEVA' || estadoGestion === 'NO_RENOVO' || tipoRenovacion === 'NO RENOVABLE') && !esAnuladaEmision(p);
            const esPendiente = ESTADOS_PENDIENTES.includes(estadoGestion) && !esAnuladaEmision(p);
            const esSinGestion = (estadoGestion === '' || estadoGestion === '-') && tipoRenovacion !== 'NO RENOVABLE' && !esAnuladaEmision(p);
            
            // Renovada efectiva (no anulada)
            const esRenovada = estadoGestion === 'RENOVADA' && !esAnuladaNueva(p);

            // Renovada pero dada de baja posteriormente
            const esRenovadaAnulada = estadoGestion === 'RENOVADA' && esAnuladaNueva(p);

            // A- Renovadas e Incentivos: Exclusivo en quienRenovo de la póliza nueva
            if (esRenovada) {
                const targetOpSalida = getOp(opEmisor);
                targetOpSalida.polizasRenovadas += 1;

                const rawPrima = p.primaEmitidaNueva !== undefined ? p.primaEmitidaNueva : (p.polizaNuevaObj?.primaEmitida || 0);
                const prima = Math.abs(parseCurrency(rawPrima));
                const monedaRaw = String(p.monedaNueva || p.polizaNuevaObj?.moneda || p.moneda || '$').toUpperCase().trim();
                const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');

                const tieneDeudaNueva = p.tieneDeudaNueva !== undefined ? p.tieneDeudaNueva : (p.polizaNuevaObj ? !!p.polizaNuevaObj.tieneDeuda : p.tieneDeuda);

                const aplicaOperador = operadorTieneIncentivo(opEmisor);
                const aplicaCanal = esCanalDirecto(p);
                const liquidaIncentivo = !tieneDeudaNueva && aplicaOperador && aplicaCanal;

                const incentivo = liquidaIncentivo ? (prima * porcentajeIncentivo) / 100 : 0;

                if (esUSD) {
                    targetOpSalida.primaUSD += prima;
                    targetOpSalida.incentivoUSD += incentivo;
                } else {
                    targetOpSalida.primaARS += prima;
                    targetOpSalida.incentivoARS += incentivo;
                }
            } 
            
            // B- Pólizas Renovadas que luego fueron Anuladas: Imputadas a quienGestiono la baja
            if (esRenovadaAnulada) {
                getOp(opBajaNueva).polizasAnuladasNuevas += 1;
            }

            // C- No Renueva, Pendientes y Sin Gestión: Exclusivo en gestionadoPor
            if (esNoRenueva) {
                getOp(opGestor).polizasNoRenueva += 1;
            } else if (esPendiente) {
                getOp(opGestor).polizasPendientes += 1;
            } else if (esSinGestion) {
                getOp(opGestor).polizasSinGestion += 1;
            }
        });

        // Cálculo de efectividad para cada operador
        return Object.values(mapOperadores).map(op => {
            const totalCartera = op.polizasRenovadas + op.polizasNoRenueva + op.polizasAnuladasNuevas + op.polizasPendientes + op.polizasSinGestion;
            const efectividad = totalCartera > 0 ? (op.polizasRenovadas / totalCartera) * 100 : 0;
            return {
                ...op,
                totalCartera,
                efectividad: Number(efectividad.toFixed(1))
            };
        }).sort((a, b) => b.polizasRenovadas - a.polizasRenovadas);
    }, [registros, isDbLoaded, porcentajeIncentivo]);

    // --- 2. DETALLE INDIVIDUAL POR OPERADOR SELECCIONADO ---
    const reporte = useMemo(() => {
        if (!isDbLoaded || !selectedOperador || !registros) {
            return { 
                polizasRenovadas: 0, 
                polizasNoRenueva: 0, 
                polizasPendientes: 0, 
                polizasSinGestion: 0, 
                polizasAnuladasNuevas: 0,
                totalCartera: 0,
                efectividad: 0,
                totalPrimaARS: 0, 
                totalPrimaUSD: 0, 
                incentivoARS: 0, 
                incentivoUSD: 0, 
                itemsRenovadosOp: [], 
                itemsNoRenueva: [], 
                itemsPendientes: [], 
                itemsSinGestion: [],
                itemsAnuladasNuevas: []
            };
        }

        // PÓLIZAS QUE EL OPERADOR EFECTIVAMENTE RENOVÓ (vigentes)
        const itemsRenovadosOp = registros.filter(r => {
            const estadoGestion = String(r.estadoGestionRenovacion || '').trim().toUpperCase();
            return estadoGestion === 'RENOVADA' && getEmisorSalida(r) === selectedOperador && !esAnuladaNueva(r);
        });

        // PÓLIZAS NUEVAS ANULADAS CUYA BAJA FUE GESTIONADA POR EL OPERADOR
        const itemsAnuladasNuevas = registros.filter(r => {
            const estadoGestion = String(r.estadoGestionRenovacion || '').trim().toUpperCase();
            return estadoGestion === 'RENOVADA' && esAnuladaNueva(r) && getGestorBajaNueva(r) === selectedOperador;
        });

        // GESTIONES ASIGNADAS AL OPERADOR POR gestionadoPor
        const itemsGestionados = registros.filter(r => getGestorEntrada(r) === selectedOperador && !esAnuladaEmision(r));

        const itemsNoRenueva = itemsGestionados.filter(p => {
            const estadoGestion = String(p.estadoGestionRenovacion || '').trim().toUpperCase();
            const tipoRenovacion = String(p.tipoRenovacion || p.formaRenovacion || '').trim().toUpperCase();
            return estadoGestion === 'NO RENUEVA' || estadoGestion === 'NO_RENOVO' || tipoRenovacion === 'NO RENOVABLE';
        });

        const itemsPendientes = itemsGestionados.filter(p => {
            const estadoGestion = String(p.estadoGestionRenovacion || '').trim().toUpperCase();
            return ESTADOS_PENDIENTES.includes(estadoGestion);
        });

        const itemsSinGestion = itemsGestionados.filter(p => {
            const estadoGestion = String(p.estadoGestionRenovacion || '').trim().toUpperCase();
            const tipoRenovacion = String(p.tipoRenovacion || p.formaRenovacion || '').trim().toUpperCase();
            return (estadoGestion === '' || estadoGestion === '-') && tipoRenovacion !== 'NO RENOVABLE';
        });

        // Total cartera individual y efectividad
        const totalCartera = itemsRenovadosOp.length + itemsNoRenueva.length + itemsAnuladasNuevas.length + itemsPendientes.length + itemsSinGestion.length;
        const efectividad = totalCartera > 0 ? Number(((itemsRenovadosOp.length / totalCartera) * 100).toFixed(1)) : 0;

        // PRIMAS E INCENTIVOS
        let totalPrimaARS = 0;
        let totalPrimaUSD = 0;
        let incentivoARS = 0;
        let incentivoUSD = 0;

        const aplicaOperador = operadorTieneIncentivo(selectedOperador);

        itemsRenovadosOp.forEach(p => {
            const rawPrima = p.primaEmitidaNueva !== undefined ? p.primaEmitidaNueva : (p.polizaNuevaObj?.primaEmitida || 0);
            const prima = Math.abs(parseCurrency(rawPrima));
            const monedaRaw = String(p.monedaNueva || p.polizaNuevaObj?.moneda || p.moneda || '$').toUpperCase().trim();
            const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');

            const tieneDeudaNueva = p.tieneDeudaNueva !== undefined ? p.tieneDeudaNueva : (p.polizaNuevaObj ? !!p.polizaNuevaObj.tieneDeuda : p.tieneDeuda);

            const aplicaCanal = esCanalDirecto(p);
            const liquidaIncentivo = !tieneDeudaNueva && aplicaOperador && aplicaCanal;
            const montoIncentivo = liquidaIncentivo ? (prima * porcentajeIncentivo) / 100 : 0;

            if (esUSD) {
                totalPrimaUSD += prima;
                incentivoUSD += montoIncentivo;
            } else {
                totalPrimaARS += prima;
                incentivoARS += montoIncentivo;
            }
        });

        return {
            polizasRenovadas: itemsRenovadosOp.length,
            polizasNoRenueva: itemsNoRenueva.length,
            polizasPendientes: itemsPendientes.length,
            polizasSinGestion: itemsSinGestion.length,
            polizasAnuladasNuevas: itemsAnuladasNuevas.length,
            totalCartera,
            efectividad,
            totalPrimaARS,
            totalPrimaUSD,
            incentivoARS,
            incentivoUSD,
            itemsRenovadosOp,
            itemsNoRenueva,
            itemsPendientes,
            itemsSinGestion,
            itemsAnuladasNuevas
        };
    }, [selectedOperador, registros, isDbLoaded, porcentajeIncentivo]);

    // --- EXPORTAR A EXCEL ---
    const exportarExcelFicha = (tipoPool = 'CARTERA_COMPLETA') => {
        const poolExport = tipoPool === 'NO_RENUEVA' ? reporte.itemsNoRenueva 
                         : tipoPool === 'PENDIENTES' ? reporte.itemsPendientes 
                         : tipoPool === 'SIN_GESTION' ? reporte.itemsSinGestion 
                         : tipoPool === 'ANULADAS_NUEVAS' ? reporte.itemsAnuladasNuevas
                         : reporte.itemsRenovadosOp;

        if (!poolExport || poolExport.length === 0) return;

        let datosExcel = [];

        if (tipoPool === 'ANULADAS_NUEVAS') {
            datosExcel = poolExport.map(item => {
                const pNueva = item.polizaNuevaObj || {};
                const rawPrima = item.primaAnulacionNueva !== undefined && item.primaAnulacionNueva !== null
                    ? item.primaAnulacionNueva 
                    : (pNueva.primaAnulacion !== undefined && pNueva.primaAnulacion !== null ? pNueva.primaAnulacion : 0);

                return {
                    'Asegurado': `${item.clienteNombre || ''} ${item.clienteApellido || ''}`.trim(),
                    'Póliza Expirante': item.nPoliza || '',
                    'Cía Expirante': item.companiaExpirante || item.compania || '',
                    'Póliza Nueva': item.nPolizaNueva || pNueva.nPoliza || '',
                    'Cía Nueva': item.companiaNueva || pNueva.compania || '',
                    'Fecha Anulación': item.fechaAnulacionNueva || pNueva.fechaAnulacion || '-',
                    'Motivo de Anulación': item.motivoAnulacionNueva || pNueva.motivoAnulacion || 'Sin motivo especificado',
                    '¿Quién Gestionó la Baja?': getGestorBajaNueva(item),
                    'Prima Anulada': Math.abs(parseCurrency(rawPrima))
                };
            });
        } else if (tipoPool === 'NO_RENUEVA' || tipoPool === 'SIN_GESTION') {
            datosExcel = poolExport.map(item => ({
                'Asegurado': `${item.clienteNombre || ''} ${item.clienteApellido || ''}`.trim(),
                'Póliza Expirante': item.nPoliza || '',
                'Compañía': item.companiaExpirante || item.compania || '',
                'Estado Renovación': item.estadoGestionRenovacion || '-',
                'Tipo de Renovacion': item.tipoRenovacion || '-',
                'Riesgo': item.riesgo || '',
                'Subriesgo': item.subriesgo || '',
                'Motivo': item.motivoNoRenueva || '-'
            }));
        } else if (tipoPool === 'PENDIENTES') {
            datosExcel = poolExport.map(item => ({
                'Asegurado': `${item.clienteNombre || ''} ${item.clienteApellido || ''}`.trim(),
                'Póliza Expirante': item.nPoliza || '',
                'Compañía': item.companiaExpirante || item.compania || '',
                'Estado Renovación': item.estadoGestionRenovacion || '-',
                'Riesgo': item.riesgo || '',
                'Subriesgo': item.subriesgo || '',
                'Estado Emisión': item.estadoEmision || ''
            }));
        } else {
            const operadorAplica = operadorTieneIncentivo(selectedOperador);

            datosExcel = poolExport.map(item => {
                const pNueva = item.polizaNuevaObj || {};
                const estadoGestionReal = (item.estadoGestionRenovacion && item.estadoGestionRenovacion !== '-') ? String(item.estadoGestionRenovacion).trim().toUpperCase() : '-';
                const esRenovada = estadoGestionReal === 'RENOVADA' && !esAnuladaNueva(item);

                const motivoVal = item.motivoNuevo || pNueva.motivo || pNueva.tipoOperacion || '';
                const subOperacionVal = item.subOperacionNueva || pNueva.subOperacion || '';
                const nPolizaNuevaVal = item.nPolizaNueva || pNueva.nPoliza || '';
                const companiaNuevaVal = item.companiaNueva || pNueva.compania || '';
                const estadoEmisionNuevaVal = item.estadoEmisionNueva || pNueva.estadoEmisionCalculado || pNueva.estadoEmision || '';
                const canalNuevaVal = item.canalNueva || pNueva.canal || item.canal || '';
                const rawPrimaVal = item.primaEmitidaNueva !== undefined ? item.primaEmitidaNueva : (pNueva.primaEmitida !== undefined ? pNueva.primaEmitida : (pNueva.premio || 0));
                const primaVal = Math.abs(parseCurrency(rawPrimaVal));
                const poseeDeudaNueva = item.tieneDeudaNueva !== undefined ? !!item.tieneDeudaNueva : (pNueva.tieneDeuda !== undefined ? !!pNueva.tieneDeuda : !!item.tieneDeuda);

                const aplicaCanal = esCanalDirecto(item);
                const incentivoItem = (esRenovada && !poseeDeudaNueva && operadorAplica && aplicaCanal) ? (primaVal * porcentajeIncentivo) / 100 : 0;

                return {
                    'Asegurado': `${item.clienteNombre || ''} ${item.clienteApellido || ''}`.trim(),
                    'Motivo Nuevo': motivoVal,
                    'Sub Operación Nueva': subOperacionVal,
                    'Riesgo': pNueva.riesgo || item.riesgo || '',
                    'Subriesgo': pNueva.subriesgo || item.subriesgo || '',
                    'Póliza Expirante': item.nPoliza || '',
                    'Cía Expirante': item.companiaExpirante || item.compania || '',
                    'Estado Emisión Anterior': item.estadoEmision || '',
                    'Tipo de Renovación': item.tipoRenovacion || '-',
                    'Estado Renovación': estadoGestionReal,
                    'Póliza Nueva': nPolizaNuevaVal,
                    'Cía Nueva': companiaNuevaVal,
                    'Canal': canalNuevaVal,
                    'Estado Emisión Nueva': estadoEmisionNuevaVal,
                    'Prima Nueva': esRenovada ? primaVal : 0,
                    'Tiene Deuda Nueva': poseeDeudaNueva ? 'SÍ' : 'NO',
                    [`Incentivo Ganado (${porcentajeIncentivo}%)`]: incentivoItem,
                };
            });
        }

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(datosExcel);
        ws['!cols'] = Object.keys(datosExcel[0] || {}).map(() => ({ wch: 22 }));
        XLSX.utils.book_append_sheet(wb, ws, tipoPool);
        XLSX.writeFile(wb, `${tipoPool}_${selectedOperador}_${new Date().toISOString().slice(0,10)}.xlsx`);
    };

    if (!isDbLoaded) {
        return (
            <div className="p-12 text-center text-slate-400 italic text-sm bg-white rounded-3xl border border-slate-200/60 shadow-sm max-w-xl mx-auto mt-16 animate-fade-in">
                Cargando rendimiento de renovaciones por responsable...
            </div>
        );
    }

    if (matrizOperadores.length === 0) {
        return (
            <div className="p-12 text-center text-slate-400 italic text-sm bg-white rounded-3xl border border-slate-200/60 shadow-sm max-w-xl mx-auto mt-16 animate-fade-in">
                No se encontraron registros de renovación en este período.
            </div>
        );
    }

    return (
        <div className="space-y-6 text-left animate-fade-in">
            
            {/* ESCENARIO A: CUADRO COMPARATIVO GENERAL */}
            {!selectedOperador ? (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden text-left flex flex-col animate-fade-in">
                    <div className="p-5 px-6 border-b border-slate-100 flex justify-between items-center text-left bg-gradient-to-r from-slate-50 to-white">
                        <div className="flex items-center gap-3">
                            <span className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl shrink-0">
                                <IconUser />
                            </span>
                            <div>
                                <h4 className="text-sm font-black tracking-tight text-[#01032d] uppercase">Renovaciones & Efectividad por Responsable</h4>
                                <p className="text-[10px] text-slate-400 font-bold mt-0.5 tracking-wider uppercase">
                                    Incentivo asignado ({porcentajeIncentivo}% - Exclusivo Canal Directo Sin Deuda)
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="overflow-x-auto w-full">
                        <table className="w-full text-xs border-collapse text-left">
                            <thead className="bg-slate-50/70 text-slate-400 border-b border-slate-100 uppercase text-[9px] font-black tracking-wider">
                                <tr>
                                    <th className="p-4 pl-6 w-16 text-center">Rango</th>
                                    <th className="p-4">Responsable</th>
                                    <th className="p-4 text-center text-indigo-600">Renovadas</th>
                                    <th className="p-4 text-center text-rose-600">No Renueva</th>
                                    <th className="p-4 text-center text-red-700">Anuladas Post-Renov.</th>
                                    <th className="p-4 text-center text-amber-600">Trámites Pendientes</th>
                                    <th className="p-4 text-center text-slate-500">Sin Gestión</th>
                                    <th className="p-4 text-center">% Efectividad</th>
                                    <th className="p-4 text-right">Prima Emitida (ARS)</th>
                                    <th className="p-4 text-right pr-6">Incentivo ({porcentajeIncentivo}%)</th>
                                </tr>
                            </thead>
                            <tbody className="font-semibold text-slate-700 divide-y divide-slate-100 bg-white">
                                {matrizOperadores.map((op, index) => {
                                    const rank = index + 1;

                                    return (
                                        <tr 
                                            key={op.nombre} 
                                            onClick={() => {
                                                setSelectedOperador(op.nombre);
                                                setSubTab('CARTERA_COMPLETA');
                                            }}
                                            className="hover:bg-blue-50/30 cursor-pointer transition-colors duration-150 group"
                                        >
                                            <td className="p-4 pl-6 text-center">
                                                {rank === 1 && <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-50 border border-amber-200 text-amber-600 font-black text-[10px]">🥇</span>}
                                                {rank === 2 && <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 border border-slate-200 text-slate-600 font-black text-[10px]">🥈</span>}
                                                {rank === 3 && <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-50 border border-orange-200 text-orange-600 font-black text-[10px]">🥉</span>}
                                                {rank > 3 && <span className="text-[10px] font-mono text-slate-400 font-bold">#{rank}</span>}
                                            </td>
                                            <td className="p-4 text-slate-800 font-bold group-hover:text-blue-900 transition-colors uppercase">
                                                <div className="flex items-center gap-2">
                                                    <span>{op.nombre}</span>
                                                    {operadorTieneIncentivo(op.nombre) && (
                                                        <span className="text-[9px] px-1.5 py-0.2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded font-black tracking-tighter" title="Habilitado para incentivos">
                                                            INC
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-4 text-center font-mono font-bold text-indigo-600">
                                                {op.polizasRenovadas}
                                            </td>
                                            <td className="p-4 text-center font-mono font-bold text-rose-600">
                                                {op.polizasNoRenueva}
                                            </td>
                                            <td className="p-4 text-center font-mono font-bold text-red-700">
                                                {op.polizasAnuladasNuevas}
                                            </td>
                                            <td className="p-4 text-center font-mono font-bold text-amber-600">
                                                {op.polizasPendientes}
                                            </td>
                                            <td className="p-4 text-center font-mono font-bold text-slate-400">
                                                {op.polizasSinGestion}
                                            </td>
                                            <td className="p-4 text-center font-mono">
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black border ${
                                                    op.efectividad >= 70 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                                    op.efectividad >= 50 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                                    'bg-rose-50 text-rose-700 border-rose-200'
                                                }`}>
                                                    {op.efectividad}%
                                                </span>
                                            </td>
                                            <td className="p-4 text-right font-mono text-emerald-600 font-bold">
                                                {op.primaARS > 0 ? formatCurrencyUniversal(op.primaARS, '$') : '-'}
                                            </td>
                                            <td className="p-4 text-right font-mono text-amber-600 pr-6 font-black">
                                                {op.incentivoARS > 0 ? formatCurrencyUniversal(op.incentivoARS, '$') : '-'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                
                /* ESCENARIO B: VISTA DE LA FICHA INDIVIDUAL CON NAVEGACIÓN POR SOLAPAS */
                <div className="space-y-6 animate-fade-in">
                    
                    {/* ENCABEZADO Y RETORNO */}
                    <div className="bg-white p-4 px-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={() => setSelectedOperador('')}
                                className="group flex items-center gap-2 p-2.5 px-4 bg-slate-100 hover:bg-slate-200/80 text-slate-700 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-200 active:scale-95"
                            >
                                <IconBack /> Volver al Resumen
                            </button>
                            <div className="border-l pl-4 border-slate-200">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-md font-black text-[#01032d] uppercase tracking-tight">Ficha de Renovación: {selectedOperador}</h3>
                                    {operadorTieneIncentivo(selectedOperador) ? (
                                        <span className="px-2 py-0.5 text-[9px] font-black uppercase rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                                            Aplica Incentivo
                                        </span>
                                    ) : (
                                        <span className="px-2 py-0.5 text-[9px] font-black uppercase rounded bg-slate-100 text-slate-500 border border-slate-200">
                                            Sin Incentivo
                                        </span>
                                    )}
                                </div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Control de producción, efectividad, incentivos y bajas procesadas ({porcentajeIncentivo}%)</p>
                            </div>
                        </div>

                        <button
                            onClick={() => exportarExcelFicha(subTab)}
                            className="flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-xs uppercase tracking-wider transition-all duration-200 shadow-sm active:scale-95"
                        >
                            <IconDownload /> Exportar Vista Excel
                        </button>
                    </div>

                    {/* METRICAS DEL OPERADOR - CARDS */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
                        <div 
                            onClick={() => setSubTab('CARTERA_COMPLETA')}
                            className={`p-4 border rounded-2xl shadow-sm flex flex-col justify-between cursor-pointer transition-all ${
                                subTab === 'CARTERA_COMPLETA' ? 'bg-indigo-50/40 border-indigo-300 ring-2 ring-indigo-500/20' : 'bg-white border-slate-200 hover:border-slate-300'
                            }`}
                        >
                            <p className="text-[9px] font-black text-indigo-500 uppercase tracking-widest">Renovadas</p>
                            <h4 className="text-xl font-black text-indigo-600 mt-2 font-mono">{reporte.polizasRenovadas}</h4>
                        </div>

                        <div 
                            onClick={() => setSubTab('NO_RENUEVA')}
                            className={`p-4 border rounded-2xl shadow-sm flex flex-col justify-between cursor-pointer transition-all ${
                                subTab === 'NO_RENUEVA' ? 'bg-rose-100/50 border-rose-300 ring-2 ring-rose-500/20' : 'bg-white border-rose-100 hover:border-rose-200'
                            }`}
                        >
                            <p className="text-[9px] font-black text-rose-500 uppercase tracking-widest">No Renueva</p>
                            <h4 className="text-xl font-black text-rose-600 mt-2 font-mono">{reporte.polizasNoRenueva}</h4>
                        </div>

                        <div 
                            onClick={() => setSubTab('ANULADAS_NUEVAS')}
                            className={`p-4 border rounded-2xl shadow-sm flex flex-col justify-between cursor-pointer transition-all ${
                                subTab === 'ANULADAS_NUEVAS' ? 'bg-red-100 border-red-400 ring-2 ring-red-500/20' : 'bg-white border-red-200 hover:border-red-300'
                            }`}
                        >
                            <p className="text-[9px] font-black text-red-700 uppercase tracking-widest">Bajas Gestionadas</p>
                            <h4 className="text-xl font-black text-red-700 mt-2 font-mono">{reporte.polizasAnuladasNuevas}</h4>
                        </div>

                        <div 
                            onClick={() => setSubTab('PENDIENTES')}
                            className={`p-4 border rounded-2xl shadow-sm flex flex-col justify-between cursor-pointer transition-all ${
                                subTab === 'PENDIENTES' ? 'bg-amber-100/50 border-amber-300 ring-2 ring-amber-500/20' : 'bg-white border-amber-100 hover:border-amber-200'
                            }`}
                        >
                            <p className="text-[9px] font-black text-amber-600 uppercase tracking-widest">Pendientes</p>
                            <h4 className="text-xl font-black text-amber-600 mt-2 font-mono">{reporte.polizasPendientes}</h4>
                        </div>

                        <div 
                            onClick={() => setSubTab('SIN_GESTION')}
                            className={`p-4 border rounded-2xl shadow-sm flex flex-col justify-between cursor-pointer transition-all ${
                                subTab === 'SIN_GESTION' ? 'bg-slate-100 border-slate-400 ring-2 ring-slate-500/20' : 'bg-white border-slate-200 hover:border-slate-300'
                            }`}
                        >
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Sin Gestión</p>
                            <h4 className="text-xl font-black text-slate-600 mt-2 font-mono">{reporte.polizasSinGestion}</h4>
                        </div>

                        {/* CARD DESTACADA: EFECTIVIDAD */}
                        <div className={`p-4 border rounded-2xl shadow-sm flex flex-col justify-between ${
                            reporte.efectividad >= 70 ? 'bg-emerald-50/50 border-emerald-300' :
                            reporte.efectividad >= 50 ? 'bg-amber-50/50 border-amber-300' :
                            'bg-rose-50/50 border-rose-300'
                        }`}>
                            <p className="text-[9px] font-black text-slate-600 uppercase tracking-widest">% Efectividad</p>
                            <h4 className={`text-xl font-black mt-2 font-mono ${
                                reporte.efectividad >= 70 ? 'text-emerald-700' :
                                reporte.efectividad >= 50 ? 'text-amber-700' :
                                'text-rose-700'
                            }`}>
                                {reporte.efectividad}%
                            </h4>
                        </div>

                        <div className="bg-white p-4 border border-emerald-100 rounded-2xl shadow-sm flex flex-col justify-between bg-emerald-50/10">
                            <p className="text-[9px] font-black text-emerald-600 uppercase tracking-widest">Prima Total</p>
                            <h4 className="text-md font-black text-emerald-600 mt-2 font-mono">
                                {formatCurrencyUniversal(reporte.totalPrimaARS, '$')}
                            </h4>
                        </div>

                        <div className="bg-white p-4 border border-amber-200 rounded-2xl shadow-sm flex flex-col justify-between bg-amber-50/20">
                            <p className="text-[9px] font-black text-amber-700 uppercase tracking-widest">Incentivo ({porcentajeIncentivo}%)</p>
                            <h4 className="text-md font-black text-amber-600 mt-2 font-mono">
                                {formatCurrencyUniversal(reporte.incentivoARS, '$')}
                            </h4>
                        </div>
                    </div>

                    {/* CONTENEDOR DE SOLAPAS Y TABLA DINÁMICA */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                        
                        <div className="p-3 px-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
                            <div className="flex bg-slate-200/60 p-1 rounded-xl gap-1">
                                <button
                                    onClick={() => setSubTab('CARTERA_COMPLETA')}
                                    className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase rounded-lg transition-all ${
                                        subTab === 'CARTERA_COMPLETA' ? 'bg-white text-[#01032d] shadow-sm font-black' : 'text-slate-600 hover:text-slate-900'
                                    }`}
                                >
                                    <IconFolder /> Renovadas ({reporte.itemsRenovadosOp.length})
                                </button>

                                <button
                                    onClick={() => setSubTab('NO_RENUEVA')}
                                    className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase rounded-lg transition-all ${
                                        subTab === 'NO_RENUEVA' ? 'bg-rose-500 text-white shadow-sm font-black' : 'text-rose-600 hover:bg-rose-50'
                                    }`}
                                >
                                    🚫 No Renueva ({reporte.itemsNoRenueva.length})
                                </button>

                                <button
                                    onClick={() => setSubTab('ANULADAS_NUEVAS')}
                                    className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase rounded-lg transition-all ${
                                        subTab === 'ANULADAS_NUEVAS' ? 'bg-red-700 text-white shadow-sm font-black' : 'text-red-700 hover:bg-red-50'
                                    }`}
                                >
                                    ⚠️ Anuladas Post-Renovación ({reporte.itemsAnuladasNuevas.length})
                                </button>

                                <button
                                    onClick={() => setSubTab('PENDIENTES')}
                                    className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase rounded-lg transition-all ${
                                        subTab === 'PENDIENTES' ? 'bg-amber-500 text-white shadow-sm font-black' : 'text-amber-600 hover:bg-amber-50'
                                    }`}
                                >
                                    ⏳ Pendientes ({reporte.itemsPendientes.length})
                                </button>

                                <button
                                    onClick={() => setSubTab('SIN_GESTION')}
                                    className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase rounded-lg transition-all ${
                                        subTab === 'SIN_GESTION' ? 'bg-slate-700 text-white shadow-sm font-black' : 'text-slate-600 hover:bg-slate-100'
                                    }`}
                                >
                                    📋 Sin Gestión ({reporte.itemsSinGestion.length})
                                </button>
                            </div>
                        </div>

                        {/* TABLAS SEGÚN LA SOLAPA SELECCIONADA */}
                        <div className="overflow-auto max-h-[460px] custom-scrollbar text-xs bg-white">
                            
                            {/* SOLAPA: ANULADAS POST-RENOVACIÓN */}
                            {subTab === 'ANULADAS_NUEVAS' && (
                                <table className="w-full text-left font-medium border-collapse min-w-[900px]">
                                    <thead className="bg-red-50 text-red-800 font-black border-b uppercase text-[9px] tracking-wider sticky top-0 z-10 shadow-[0_1px_0_0_rgba(226,232,240,1)]">
                                        <tr>
                                            <th className="p-3 pl-4 text-left">Asegurado</th>
                                            <th className="p-3 text-center">Póliza Expirante</th>
                                            <th className="p-3 text-center">Póliza Nueva</th>
                                            <th className="p-3 text-center">Compañía</th>
                                            <th className="p-3 text-center">Fecha Anulación</th>
                                            <th className="p-3 text-center">Motivo de Anulación</th>
                                            <th className="p-3 text-center">Gestor de la Baja</th>
                                            <th className="p-3 text-center">Prima de Anulación</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 text-slate-600 font-semibold">
                                        {reporte.itemsAnuladasNuevas.length === 0 ? (
                                            <tr>
                                                <td colSpan="8" className="p-12 text-center text-slate-400 italic">No hay pólizas nuevas anuladas gestionadas por este operador.</td>
                                            </tr>
                                        ) : (
                                            reporte.itemsAnuladasNuevas.map((item, i) => {
                                                const pNueva = item.polizaNuevaObj || {};
                                                const motivoAnul = item.motivoAnulacionNueva || pNueva.motivoAnulacion || item.motivoAnulacion || 'Sin motivo';
                                                const fechaAnul = item.fechaAnulacionNueva || pNueva.fechaAnulacion || item.fechaAnulacion || '-';
                                                const rawPrima = item.primaAnulacionNueva !== undefined && item.primaAnulacionNueva !== null
                                                    ? item.primaAnulacionNueva 
                                                    : (pNueva.primaAnulacion !== undefined && pNueva.primaAnulacion !== null ? pNueva.primaAnulacion : 0);

                                                const primaVal = Math.abs(parseCurrency(rawPrima));

                                                return (
                                                    <tr key={item.id || i} className="hover:bg-red-50/20 transition-colors">
                                                        <td className="p-3 pl-4 font-bold text-slate-800">{item.clienteNombre} {item.clienteApellido}</td>
                                                        <td className="p-3 font-mono text-slate-500 text-center">{item.nPoliza || '-'}</td>
                                                        <td className="p-3 font-mono font-bold text-red-700 text-center">{item.nPolizaNueva || pNueva.nPoliza || '-'}</td>
                                                        <td className="p-3 uppercase text-center">{item.companiaNueva || pNueva.compania || item.compania || '-'}</td>
                                                        <td className="p-3 text-center font-mono">{fechaAnul}</td>
                                                        <td className="p-3 text-center">
                                                            <span className="px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-red-100 text-red-800 border border-red-200">
                                                                {motivoAnul}
                                                            </span>
                                                        </td>
                                                        <td className="p-3 uppercase text-center font-bold text-slate-700">{getGestorBajaNueva(item)}</td>
                                                        <td className="p-3 text-right pr-6 font-mono font-bold text-slate-600">
                                                            {primaVal > 0 ? formatCurrencyUniversal(primaVal, '$') : '-'}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            )}

                            {/* SOLAPA: NO RENUEVA */}
                            {subTab === 'NO_RENUEVA' && (
                                <table className="w-full text-left font-medium border-collapse min-w-[700px]">
                                    <thead className="bg-rose-50/50 text-rose-500 font-black border-b uppercase text-[9px] tracking-wider sticky top-0 z-10 shadow-[0_1px_0_0_rgba(226,232,240,1)]">
                                        <tr>
                                            <th className="p-3 pl-4 text-left">Asegurado</th>
                                            <th className="p-3 text-center">Póliza</th>
                                            <th className="p-3 text-center">Compañía</th>
                                            <th className="p-3 text-center">Estado Renovación</th>
                                            <th className="p-3 text-center">Tipo de Renovación</th>
                                            <th className="p-3 text-center">Riesgo</th>
                                            <th className="p-3 text-center pr-6">Subriesgo</th>
                                            <th className="p-3 text-center pr-6">Motivo</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 text-slate-600 font-semibold">
                                        {reporte.itemsNoRenueva.length === 0 ? (
                                            <tr>
                                                <td colSpan="8" className="p-12 text-center text-slate-400 italic">No hay pólizas en estado NO RENUEVA.</td>
                                            </tr>
                                        ) : (
                                            reporte.itemsNoRenueva.map((item, i) => (
                                                <tr key={item.id || i} className="hover:bg-rose-50/20 transition-colors">
                                                    <td className="p-3 pl-4 font-bold text-slate-800">{item.clienteNombre} {item.clienteApellido}</td>
                                                    <td className="p-3 font-mono font-bold text-slate-600 text-center">{item.nPoliza || '-'}</td>
                                                    <td className="p-3 uppercase text-center">{item.companiaExpirante || item.compania || '-'}</td>
                                                    <td className="p-3 text-center">
                                                        <span className="px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-rose-50 text-rose-700 border border-rose-200">
                                                            {item.estadoGestionRenovacion || '-'}
                                                        </span>
                                                    </td>
                                                    <td className="p-3 text-center">
                                                        <span className="px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-rose-50 text-rose-700 border border-rose-200">
                                                            {item.tipoRenovacion || '-'}
                                                        </span>
                                                    </td>
                                                    <td className="p-3 uppercase text-center">{item.riesgo || '-'}</td>
                                                    <td className="p-3 uppercase text-center pr-6">{item.subriesgo || '-'}</td>
                                                    <td className="p-3 uppercase text-center pr-6">{item.motivoNoRenueva || '-'}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            )}

                            {/* SOLAPA: TRÁMITES PENDIENTES */}
                            {subTab === 'PENDIENTES' && (
                                <table className="w-full text-left font-medium border-collapse min-w-[800px]">
                                    <thead className="bg-amber-50/50 text-amber-600 font-black border-b uppercase text-[9px] tracking-wider sticky top-0 z-10 shadow-[0_1px_0_0_rgba(226,232,240,1)]">
                                        <tr>
                                            <th className="p-3 pl-4 text-left">Asegurado</th>
                                            <th className="p-3 text-center">Póliza</th>
                                            <th className="p-3 text-center">Compañía</th>
                                            <th className="p-3 text-center">Estado Renovación</th>
                                            <th className="p-3 text-center">Riesgo</th>
                                            <th className="p-3 text-center">Subriesgo</th>
                                            <th className="p-3 text-center pr-6">Estado Emisión</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 text-slate-600 font-semibold">
                                        {reporte.itemsPendientes.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="p-12 text-center text-slate-400 italic">No hay pólizas en trámites pendientes.</td>
                                            </tr>
                                        ) : (
                                            reporte.itemsPendientes.map((item, i) => (
                                                <tr key={item.id || i} className="hover:bg-amber-50/20 transition-colors">
                                                    <td className="p-3 pl-4 font-bold text-slate-800">{item.clienteNombre} {item.clienteApellido}</td>
                                                    <td className="p-3 font-mono font-bold text-slate-600 text-center">{item.nPoliza || '-'}</td>
                                                    <td className="p-3 uppercase text-center">{item.companiaExpirante || item.compania || '-'}</td>
                                                    <td className="p-3 text-center">
                                                        <span className="px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-amber-50 text-amber-700 border border-amber-200">
                                                            {item.estadoGestionRenovacion || '-'}
                                                        </span>
                                                    </td>
                                                    <td className="p-3 uppercase text-center">{item.riesgo || '-'}</td>
                                                    <td className="p-3 uppercase text-center">{item.subriesgo || '-'}</td>
                                                    <td className="p-3 text-center pr-6">
                                                        <span className="px-2 py-0.5 rounded-md text-[9px] font-extrabold uppercase bg-slate-100 text-slate-600">
                                                            {item.estadoEmision || '-'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            )}

                            {/* SOLAPA: SIN GESTIÓN */}
                            {subTab === 'SIN_GESTION' && (
                                <table className="w-full text-left font-medium border-collapse min-w-[700px]">
                                    <thead className="bg-slate-100 text-slate-600 font-black border-b uppercase text-[9px] tracking-wider sticky top-0 z-10 shadow-[0_1px_0_0_rgba(226,232,240,1)]">
                                        <tr>
                                            <th className="p-3 pl-4 text-left">Asegurado</th>
                                            <th className="p-3 text-center">Póliza Expirante</th>
                                            <th className="p-3 text-center">Compañía</th>
                                            <th className="p-3 text-center">Estado Renovación</th>
                                            <th className="p-3 text-center">Riesgo</th>
                                            <th className="p-3 text-center pr-6">Subriesgo</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 text-slate-600 font-semibold">
                                        {reporte.itemsSinGestion.length === 0 ? (
                                            <tr>
                                                <td colSpan="6" className="p-12 text-center text-slate-400 italic">No hay pólizas sin gestión pendiente.</td>
                                            </tr>
                                        ) : (
                                            reporte.itemsSinGestion.map((item, i) => (
                                                <tr key={item.id || i} className="hover:bg-slate-50 transition-colors">
                                                    <td className="p-3 pl-4 font-bold text-slate-800">{item.clienteNombre} {item.clienteApellido}</td>
                                                    <td className="p-3 font-mono font-bold text-slate-600 text-center">{item.nPoliza || '-'}</td>
                                                    <td className="p-3 uppercase text-center">{item.companiaExpirante || item.compania || '-'}</td>
                                                    <td className="p-3 text-center font-bold text-slate-400">-</td>
                                                    <td className="p-3 uppercase text-center">{item.riesgo || '-'}</td>
                                                    <td className="p-3 uppercase text-center pr-6">{item.subriesgo || '-'}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            )}

                            {/* SOLAPA PRINCIPAL: MUESTRA ÚNICAMENTE LO RENOVADO POR EL OPERADOR SELECCIONADO */}
                            {subTab === 'CARTERA_COMPLETA' && (
                                <table className="w-full text-left font-medium border-collapse min-w-[1450px] table-fixed">
                                    <thead className="bg-slate-50 text-slate-400 font-black border-b uppercase text-[9px] tracking-wider sticky top-0 z-10 shadow-[0_1px_0_0_rgba(226,232,240,1)]">
                                        <tr>
                                            <th className="p-3 pl-4 w-44 text-left">Asegurado</th>
                                            <th className="p-3 text-center w-28">Motivo</th>
                                            <th className="p-3 text-center w-28">Sub Operación</th>
                                            <th className="p-3 text-center w-28">Riesgo</th>
                                            <th className="p-3 text-center w-28">Subriesgo</th>
                                            <th className="p-3 text-center w-28">Póliza Expirante</th>
                                            <th className="p-3 text-center w-28">Cía Expirante</th>
                                            <th className="p-3 text-center w-32">Estado Emisión Anterior</th>
                                            <th className="p-3 text-center w-32">Tipo de Renovación</th>
                                            <th className="p-3 text-center w-32">Estado Renovación</th>
                                            <th className="p-3 text-center w-28">Póliza Nueva</th>
                                            <th className="p-3 text-center w-28">Cía Nueva</th>
                                            <th className="p-3 text-center w-24">Canal</th>
                                            <th className="p-3 text-center w-32">Estado Emisión Nueva</th>
                                            <th className="p-3 text-right w-32">Prima Nueva</th>
                                            <th className="p-3 text-center w-24">Tiene Deuda</th>
                                            <th className="p-3 text-right pr-6 w-32">Incentivo ({porcentajeIncentivo}%)</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 text-slate-600 font-semibold">
                                        {reporte.itemsRenovadosOp.length === 0 ? (
                                            <tr>
                                                <td colSpan="17" className="p-12 text-center text-slate-400 italic">No hay pólizas renovadas para este operador.</td>
                                            </tr>
                                        ) : (
                                            reporte.itemsRenovadosOp.map((item, i) => {
                                                const pNueva = item.polizaNuevaObj || {};
                                                const motivoVal = item.motivoNuevo || pNueva.motivo || pNueva.tipoOperacion || '';
                                                const subOperacionVal = item.subOperacionNueva || pNueva.subOperacion || '';
                                                const nPolizaNuevaVal = item.nPolizaNueva || pNueva.nPoliza || '';
                                                const companiaNuevaVal = item.companiaNueva || pNueva.compania || '';
                                                const canalVal = item.canalNueva || pNueva.canal || item.canal || '';
                                                const estadoEmisionNuevaVal = item.estadoEmisionNueva || pNueva.estadoEmisionCalculado || pNueva.estadoEmision || '';
                                                const rawPrimaVal = item.primaEmitidaNueva !== undefined ? item.primaEmitidaNueva : (pNueva.primaEmitida !== undefined ? pNueva.primaEmitida : (pNueva.premio || 0));
                                                const primaVal = Math.abs(parseCurrency(rawPrimaVal));
                                                const poseeDeudaNueva = item.tieneDeudaNueva !== undefined ? !!item.tieneDeudaNueva : (pNueva.tieneDeuda !== undefined ? !!pNueva.tieneDeuda : !!item.tieneDeuda);

                                                const isUSD = String(item.monedaNueva || pNueva.moneda || item.moneda || '$').toUpperCase().includes('USD');

                                                const aplicaOperador = operadorTieneIncentivo(selectedOperador);
                                                const aplicaCanal = esCanalDirecto(item);
                                                const incentivoItem = (!poseeDeudaNueva && aplicaOperador && aplicaCanal) ? (primaVal * porcentajeIncentivo) / 100 : 0;

                                                return (
                                                    <tr key={item.id || i} className="hover:bg-slate-50/40 transition-colors duration-150">
                                                        <td className="p-3 pl-4 font-bold text-slate-800 truncate text-left">
                                                            {item.clienteNombre} {item.clienteApellido}
                                                        </td>
                                                        <td className="p-3 uppercase text-center truncate text-[11px]">{motivoVal || '-'}</td>
                                                        <td className="p-3 uppercase text-center truncate text-[11px]">{subOperacionVal || '-'}</td>
                                                        <td className="p-3 uppercase text-center truncate text-[11px]">{pNueva.riesgo || item.riesgo || '-'}</td>
                                                        <td className="p-3 uppercase text-center truncate text-[11px]">{pNueva.subriesgo || item.subriesgo || '-'}</td>
                                                        <td className="p-3 font-mono font-bold text-slate-600 text-center">{item.nPoliza || '-'}</td>
                                                        <td className="p-3 uppercase text-center truncate text-[11px]">{item.companiaExpirante || item.compania || '-'}</td>
                                                        <td className="p-3 text-center">
                                                            <span className={`px-2 py-0.5 rounded-md text-[9px] font-extrabold uppercase ${
                                                                item.estadoEmision === 'VENCIDA' ? 'bg-amber-100/60 text-amber-800' :
                                                                item.estadoEmision === 'ANULADA' || item.estadoEmision === 'CANCELADA' ? 'bg-rose-100/60 text-rose-800' :
                                                                'bg-emerald-100/60 text-emerald-800'
                                                            }`}>
                                                                {item.estadoEmision || '-'}
                                                            </span>
                                                        </td>
                                                        <td className="p-3 uppercase text-center truncate text-[11px]">{item.tipoRenovacion || item.formaRenovacion || '-'}</td>
                                                        <td className="p-3 text-center">
                                                            <span className="px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                                RENOVADA
                                                            </span>
                                                        </td>
                                                        <td className="p-3 font-mono font-bold text-indigo-600 text-center">{nPolizaNuevaVal || '-'}</td>
                                                        <td className="p-3 uppercase text-center truncate text-[11px]">{companiaNuevaVal || '-'}</td>
                                                        <td className="p-3 uppercase text-center truncate text-[11px]">
                                                            <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${
                                                                aplicaCanal ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'text-slate-500'
                                                            }`}>
                                                                {canalVal || '-'}
                                                            </span>
                                                        </td>
                                                        <td className="p-3 text-center">
                                                            <span className={`px-2 py-0.5 rounded-md text-[9px] font-extrabold uppercase ${
                                                                estadoEmisionNuevaVal === 'VIGENTE' ? 'bg-emerald-100/60 text-emerald-800' :
                                                                estadoEmisionNuevaVal === 'VENCIDA' ? 'bg-amber-100/60 text-amber-800' :
                                                                estadoEmisionNuevaVal === 'ANULADA' || estadoEmisionNuevaVal === 'CANCELADA' ? 'bg-rose-100/60 text-rose-800' :
                                                                'bg-slate-100 text-slate-600'
                                                            }`}>
                                                                {estadoEmisionNuevaVal || '-'}
                                                            </span>
                                                        </td>
                                                        <td className="p-3 text-right font-mono font-bold text-emerald-600">
                                                            {primaVal > 0 ? formatCurrencyUniversal(primaVal, isUSD ? 'U$S' : '$') : '-'}
                                                        </td>
                                                        <td className="p-3 text-center">
                                                            <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${
                                                                poseeDeudaNueva ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'
                                                            }`}>
                                                                {poseeDeudaNueva ? 'SÍ' : 'NO'}
                                                            </span>
                                                        </td>
                                                        <td className="p-3 text-right pr-6 font-mono font-black text-amber-600">
                                                            {incentivoItem > 0 ? formatCurrencyUniversal(incentivoItem, isUSD ? 'U$S' : '$') : '-'}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            )}

                        </div>
                    </div>

                </div>
            )}

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .animate-fade-in { animation: fadeIn 0.25s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default RendimientoRenovacionesVendedores;